import { Module, ThermOfUse, PersonMethodsTypes, ThermOfUseAdherence } from "@idha/idha-types";
import { utilsModule } from "../utils-module";


/**
  * @class
  * @name app.ThermOfUseAdherenceService
  * 
  * @description
  * Classe que descreve o serviço thermOfUseAdherenceService, responsável por 
  * verificar os termos de uso de uma Pessoa para um módulo, funcionando como interface com
  * o componente CMP0015.
  * 
  * Esta classe não deve ser construída manualmente. O ideal é que seja utilizada como signgleton,
  * através da injeção do serviço thermOfUseAdherenceService
  * 
  * @see http://wiki.idha.com.br/index.php/CMP0015_-_Di%C3%A1logo_de_Aceite_dos_Termos_de_Uso
  *
**/
export class ThermOfUseAdherenceService{
    
    /**
     * Construtor da classe, responsável por injetar os serviços necessários para correto funcionamento
     * @param Module serviço para consulta de Módulos através da API com o manager
     * @param Person serviço para consulta de Pessoas através da API com o manager
     * @param ThermOfUse serviço para consulta de Termos de Uso através da API com o manager
     * @param ThermOfUseAdherence serviço para manipulação de adesão de Termos de Uso através da API com o manager
     * @param $mdDialog serviço de dialogos do angularjs material
     * @param $sce Strict Contextual Escaping services para AngularJS.
     */
    constructor(private Module:any,private Person:any,private ThermOfUse:any,private ThermOfUseAdherence:any,private $mdDialog:any,private $sce:any){
        //Do Nothing
    }

    /**
     * Retorna o texto do termo de uso mais recente para um modulo
     * @param {ThermModules} moduleName nome do módulo a ser verificado
     * @returns {Promise} promise resolvida com o texto do termo de uso
     */
    async getThermOfUse(moduleName:ThermModules):Promise<string>{
        let module:Module = await this.Module.findOne({filter:{where:{name:moduleName}}}).$promise;

        //Obtém o termo de uso mais recente para o módulo
        let thermsOfUse:ThermOfUse[] = await this.ThermOfUse.find({
            skipOwnerId: true, 
            filter: {
                where: {
                    moduleId:module.id
                },
                include:['module']
            }
        }).$promise;

        let lastThermOfUse:ThermOfUse = thermsOfUse.reduce((last:ThermOfUse,current:ThermOfUse)=>{
            if(!current && !last) return undefined;
            if(!last) return current;
            return (current.version > last.version) ? current : last;
        },undefined);

        return lastThermOfUse.text
    }

    /**
     * Verifica se o usuário precisa realizar o aceite em alguma versão do termo de uso para um módulo
     * @param {string} personId ID da pessoa a ser verificada
     * @param {ThermModules} moduleName nome do módulo a ser verificado
     * @returns {Promise} promise resolvida caso o termo de uso esteja em dia ou caso o usuário tenha aderido quando solicitado.
     * Rejeita caso haja algum erro ou caso o usuário rejeite o termo de uso. A resolução da promise recebe true quando o usuario acabou de aceitar
     * os termos de uso, ou false caso o mesmo ja estava em dia.
     * 
     * @throws ErrorThermOfUseRejected caso o usuário rejeite os termos
     * @throws ErrorThermOfUseFailDisplay caso haja algum erro ao exibir os termos de uso
     * @throws ErrorThermOfUseFailAdherence caso haja algum erro na hora de aderir aos termos de uso
     */
    checkAdherence(personId:string,moduleName:ThermModules):Promise<boolean>{
        return new Promise(async(resolve,reject)=>{
            try{
                var self = this;

                //obtém o módulo
                let module:Module = await this.Module.findOne({filter:{where:{name:moduleName}}}).$promise;

                //Obtém o termo de uso mais recente para o módulo
                let thermsOfUse:ThermOfUse[] = await this.ThermOfUse.find({
                    skipOwnerId: true, 
                    filter: {
                        where: {
                            moduleId:module.id
                        },
                        include:['module']
                    }
                }).$promise;

                let lastThermOfUse:ThermOfUse = thermsOfUse.reduce((last:ThermOfUse,current:ThermOfUse)=>{
                    if(!current && !last) return undefined;
                    if(!last) return current;
                    return (current.version > last.version) ? current : last;
                },undefined);


                if(lastThermOfUse){                    
                    //Obtém a ultima adesão do usuário
                    let lastAdherenceResult:PersonMethodsTypes.LastThermOfUseAdherenceOutput = await this.Person.lastThermOfUseAdherence({id:personId,moduleId:module.id}).$promise;
                    let lastAdherence:ThermOfUse = lastAdherenceResult.lastAdheredThermOfUse || undefined;

                    if(lastAdherence){
                        if(lastAdherence.version < lastThermOfUse.version){
                            await self.openDialog(this.$mdDialog,lastThermOfUse,module.name,'thermUpdate');
                        }else{
                            return resolve(false);
                        }
                    }else{
                        await self.openDialog(this.$mdDialog,lastThermOfUse,module.name,'firstAdherence');
                    }

                    await self.adhere(personId,lastThermOfUse);
                    resolve(true);
                }else{
                    resolve(false);
                }
            }catch(e){
                if(e instanceof ErrorThermOfUse ){
                    reject(e)
                }else{
                    reject(new ErrorThermOfUseFailDisplay(moduleName));
                }                
            }            
        })        
        
    }

    /**
     * Exibe a versão mais atual do termo de uso de um módulo para que o usuário realize o aceite.
     * IMPORTANTE: Não registra a adesão no banco de dados
     * @param {ThermModules} moduleName nome do módulo para o qual os termos de uso serao exibidos
     * @returns {Promise} promise resolvida caso o usuario aceite (ou caso não haja termo de uso para o modulo), ou rejeitada caso contrário. 
     *                    A promise recebe por parametro a instancia do termo de uso, ou false, caso não haja.
     * @throws ErrorThermOfUseRejected caso o usuário rejeite os termos
     * @throws ErrorThermOfUseFailDisplay caso haja algum erro ao exibir os termos de uso
     */
    showThermsOfUseDlg(moduleName:ThermModules):Promise<any|false>{
        return new Promise(async(resolve,reject)=>{
            try{
                var self = this;

                //obtém o módulo
                let module:Module = await this.Module.findOne({filter:{where:{name:moduleName}}}).$promise;

                //Obtém o termo de uso mais recente para o módulo
                let thermsOfUse:ThermOfUse[] = await this.ThermOfUse.find({
                    skipOwnerId: true, 
                    filter: {
                        where: {
                            moduleId:module.id
                        }
                    }
                }).$promise;

                let lastThermOfUse:ThermOfUse = thermsOfUse.reduce((last:ThermOfUse,current:ThermOfUse)=>{
                    if(!current && !last) return undefined;
                    if(!last) return current;
                    return (current.version > last.version) ? current : last;
                },undefined);



                if(lastThermOfUse){                                        
                    await self.openDialog(this.$mdDialog,lastThermOfUse,module.name,'firstAdherence');
                    resolve(lastThermOfUse);
                }else{
                    resolve(false);
                }                
            }catch(e){
                if(e instanceof ErrorThermOfUse ){
                    reject(e)
                }else{
                    reject(new ErrorThermOfUseFailDisplay(moduleName));
                }                
            }            
        })        
        
    }

    /**
     * Adere a um termo de uso
     * @param {string} personId ID da pessoa a aderir
     * @param {ThermModules} thermOfUse termo de uso a ser aderido
     * @returns {Promise} promise resolvida caso o termo de uso seja aderido com sucesso.
     * 
     * @throws ErrorThermOfUseFailAdherence caso haja algum erro na hora de aderir aos termos de uso
     */
    async adhere(personId:string,thermOfUse:ThermOfUse):Promise<any>{
        var self = this;
    
        let adherence:ThermOfUseAdherence = {
            personId:personId,
            thermOfUseId:thermOfUse.id
        }
        try{
            await self.ThermOfUseAdherence.save(adherence).$promise;
        }catch(e){
            throw new ErrorThermOfUseFailAdherence(thermOfUse.module ? thermOfUse.module.name : undefined);
        }                                  
    }

    /**
     * Exibe os termos de uso em um diálogo para que o usuário informe se adere ou não.
     * @param {ThermModules} thermOfUse termo de uso a ser exibido
     * @returns {Promise} promise resolvida caso o usuário marque que leu e concorda e, em seguida, clique em confirmar.
     * 
     * @throws ErrorThermOfUseFailAdherence caso haja algum erro na hora de aderir aos termos de uso
     */
    private async openDialog($mdDialog:any,thermOfUse:ThermOfUse,moduleName:string,type:'thermUpdate'|'firstAdherence'):Promise<any>{
        let title = 'THERM_OF_USE';
        var self = this;

        switch(type){                
            case 'thermUpdate':
                title = `${title}_${moduleName.toUpperCase()}_UPDATE`;
                break;
            case 'firstAdherence':                    
                title = `${title}_${moduleName.toUpperCase()}_FIRST_ADHERENCE`;
                break;
        }
        try{
            await $mdDialog.show({
                template:require('./therm-of-use-adherence.dialog.tmpl.html'),
                controller:['$scope',function($scope){
                    var ctrl = this;
                    $scope.$ctrl = this;

                    ctrl.thermOfUse = self.$sce.trustAsHtml(thermOfUse.text);
                    ctrl.title = title;

                    ctrl.cancel = function(){
                        $mdDialog.cancel();
                    }
                    
                    ctrl.confirm = function(result:any){
                        $mdDialog.hide();
                    }
                }]
            });
        }catch(e){
            throw new ErrorThermOfUseRejected(moduleName);
        }
    }

}


/**
 * @type
 * @name app.ThermModules
 * @description
 * Módulos disponiveis para verificação dos termos de uso.
 * 
 * * Tags: módulo de etiquetas
 * * packing: módulo de embalagens
 * * management: módulo gestor
 */
export type ThermModules = 'tags'|'packing'|'management'



/**
 * @error
 * @class
 * @description
 * Classe base dos erros lançados ao realizar as verificações de termos de uso
 */
export abstract class ErrorThermOfUse extends Error{
    private moduleName:string;

    public getModuleName():string{
        return this.moduleName;
    }

    constructor(message:string,moduleName:string){
        super(message);
        this.moduleName = moduleName;        
    }
}

/**
 * @error
 * @class
 * @description
 * Erro lançado caso o usuário rejeite o termo de uso
 */
export class ErrorThermOfUseRejected extends ErrorThermOfUse{
    constructor(moduleName:string){
        let msg = 'FAIL_THERMS_OF_USE_REJECTED_ADHERENCE';
        super(msg,moduleName);
    }
}

/**
 * @error
 * @class
 * @description
 * Erro lançado caso haja algum erro ao realizar adesão aos termos de uso
 */
export class ErrorThermOfUseFailAdherence extends ErrorThermOfUse{
    constructor(moduleName:string){
        let msg = 'FAIL_THERMS_OF_USE_ADHERENCE';
        super(msg,moduleName);
    }
}

/**
 * @error
 * @class
 * @description
 * Erro lançado caso haja algum erro ao exibir os termos de uso (erro nas consutas ou qualquer outro tipo de excessão)
 */
export class ErrorThermOfUseFailDisplay extends ErrorThermOfUse{
    constructor(moduleName:string){
        let msg = 'FAIL_THERMS_OF_USE_DISPLAY';
        super(msg,moduleName);
    }
}


/**
  * @ngdoc service
  * @interface
  * @name app.thermOfUseAdherenceService
  * 
  * @description
  * Registra um singleton para verificações de assinatura de termo de uso.
  * O singleton registrado é uma instância de  ThermOfUseAdherenceService.
  * 
  * @see http://wiki.idha.com.br/index.php/CMP0015_-_Di%C3%A1logo_de_Aceite_dos_Termos_de_Uso
  * @see app.ThermOfUseAdherenceService
  *
  * @example
  * thermOfUseAdherenceService.checkAdherence(myPerson.id,'tags')
**/
utilsModule.service('thermOfUseAdherenceService',['Module','Person','ThermOfUse','ThermOfUseAdherence','$mdDialog','$sce',ThermOfUseAdherenceService])